import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import type { TeddyKey } from '../teddies.js'
import type { SkillLevels } from './maths-skills.js'
import { levelOf, MAX_LEVEL, MIN_LEVEL } from './maths-skills.js'
import { buildSudoku, cellsToFill, type SudokuPuzzle } from './sudoku-puzzle.js'
import { sudokuShapeFor, sudokuSkillOf } from './sudoku-skills.js'
import {
  conflictsIn,
  EMPTY,
  isGridSolved,
  shapeOf,
  SUDOKU_SIZES,
  valuesOf,
  type Grid,
  type SudokuSize,
} from './sudoku-solve.js'

/**
 * Stripey's Sudoku.
 *
 * One grid, not a queue of questions — which makes it the odd one out in this
 * roster and explains most of what follows. Monkey's, Turtley's and Rocky's
 * each ask six things and mark each one; this asks one thing for ten minutes.
 *
 * Two rules it takes from the ticket and does not bend:
 *
 * **Given cells cannot be changed.** They are the puzzle, not her working.
 *
 * **A wrong cell is never refused, and never marked wrong.** She may put Sky
 * where Rocky goes. The cell shows as conflicting if it duplicates another in
 * its row, column or box, and says nothing at all otherwise — even when it
 * differs from the solution. Being told "that's not it" the instant she writes
 * something is being handed the answer one cell at a time, and the whole game
 * is finding out for yourself.
 *
 * She is allowed to be wrong on the way to being right.
 */

/** The largest grid there is, so the schema cannot drift from the sizes. */
const LARGEST_GRID = SUDOKU_SIZES.reduce((most, size) => (size > most ? size : most))

export const DEFAULT_SUDOKU_SIZE: SudokuSize = 4

export const sudokuAction = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('place'),
    cell: z
      .number()
      .int()
      .min(0)
      .max(LARGEST_GRID * LARGEST_GRID - 1),
    /**
     * What to put there, or null to rub that one square out.
     *
     * Rubbing out is the same action rather than one of its own, because it is
     * the same gesture: she is saying what this square holds, and sometimes the
     * answer is nothing. One action means one place where givens are protected.
     */
    value: z.number().int().min(1).max(LARGEST_GRID).nullable(),
  }),
  /**
   * Rub out everything she has put in, and leave the puzzle.
   *
   * An action, though it is tempting not to make it one — nothing about the
   * round changes except her own working, and a room would have nowhere to
   * send it. It has to be one anyway, because the server scores a round by
   * replaying the moves it was told about: a reset the server never heard of
   * would leave its grid holding cells hers no longer has, every later
   * placement would land on a different board, and a puzzle she finished would
   * replay as one she did not.
   */
  z.object({ type: z.literal('clear') }),
])

export type SudokuAction = z.infer<typeof sudokuAction>

export interface SudokuConfig {
  readonly size?: SudokuSize
  readonly levels?: SkillLevels
  readonly startedAt?: number
}

/**
 * The round, as a parent will read it months later.
 *
 * **One of these per grid, not one per cell.** Every other game in the roster
 * writes a row per question, and copying that here was the first design: cell
 * filled, what she put, what belonged there. It is wrong twice over.
 *
 * A 9×9 would file sixty rows and bury every other game's handful, on a page
 * whose whole value is being readable at a glance. And most of those rows
 * would libel her: a child who tries Owlie, sees the clash and puts Nemo there
 * instead has done the puzzle exactly right, and recording that as a wrong
 * answer followed by a right one describes good reasoning as flailing.
 * Changing your mind is *working*, not answering.
 *
 * So what is recorded is the grid: how big, how it went, how long it took, and
 * how much rubbing out there was.
 */
export interface SudokuAttempt {
  readonly skillKey: string
  readonly prompt: string
  readonly expected: number
  readonly given: number
  readonly correct: boolean
  readonly elapsedMs: number
  readonly playerId: string
}

export interface SudokuState {
  readonly puzzle: SudokuPuzzle
  /** The grid as it stands: givens, plus whatever she has put in. */
  readonly grid: Grid
  readonly levels: SkillLevels
  readonly status: 'playing' | 'finished'
  readonly turn: TurnState
  readonly startedAt: number
  /**
   * How many times she has typed over her own work or rubbed it out.
   *
   * Counted rather than recorded one by one. It is a measure of how much the
   * grid was a fight, and no parent wants sixty rows of somebody thinking.
   */
  readonly changes: number
}

/** One cell, as a client is allowed to see it. */
export interface SudokuCellView {
  readonly cell: number
  /** Null for an empty cell. Never the solution's value for an empty one. */
  readonly value: number | null
  readonly given: boolean
  /** Duplicates another in its row, column or box. Not "differs from the answer". */
  readonly conflicting: boolean
}

export interface SudokuView {
  readonly size: SudokuSize
  readonly cells: readonly SudokuCellView[]
  /**
   * Which teddy each value is drawn as, value 1 first. Empty at 9×9.
   *
   * The only place the drawing exists. The engine above reasons entirely in
   * numbers, which is why the biggest grid needs no special case.
   */
  readonly teddies: readonly TeddyKey[]
  readonly status: SudokuState['status']
  readonly currentPlayerId: string | null
  /** Cells she has filled, of the cells there are to fill. */
  readonly filled: number
  readonly toFill: number
  /** Cells that clash right now, so a screen can say "two of those are Sky". */
  readonly conflicts: number
  readonly changes: number
  /**
   * How many of each value are still to be placed, value 1 first.
   *
   * Counted across the whole grid, givens included, because what she wants to
   * know is how many are left to put in — and a teddy the puzzle gave her four
   * of is a teddy she has two of to place, not six.
   *
   * **It says nothing about whether she is right.** It counts what is on the
   * grid, which she can see, so a screen using it to dim a spent teddy is
   * repeating something already in front of her rather than marking her work.
   * Six of a teddy placed wrongly reads as spent, and should: she has none
   * left to put in until she takes one out.
   */
  readonly remaining: readonly number[]
}

/**
 * What a client is allowed to see.
 *
 * The solution never leaves this function, and nor does the puzzle's own idea
 * of what belongs in an empty cell. Solo play runs this engine in her own
 * browser, so that is a discipline rather than a defence — but it is the
 * discipline that makes the rule above true in code as well as in intention:
 * nothing downstream *can* mark a cell against the answer, because nothing
 * downstream has it.
 */
export function toSudokuView(state: SudokuState): SudokuView {
  const geometry = shapeOf(state.puzzle.size)
  const clashing = conflictsIn(state.grid, geometry)

  return {
    size: state.puzzle.size,
    cells: state.grid.map((value, cell) => ({
      cell,
      value: value === EMPTY ? null : value,
      given: state.puzzle.givens[cell] !== EMPTY,
      conflicting: clashing.has(cell),
    })),
    teddies: state.puzzle.teddies,
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    filled: state.grid.filter((value, cell) => value !== EMPTY && !isGiven(state, cell)).length,
    toFill: cellsToFill(state.puzzle),
    conflicts: clashing.size,
    changes: state.changes,
    remaining: valuesOf(geometry).map((value) => {
      const placed = state.grid.filter((each) => each === value).length

      return Math.max(0, state.puzzle.size - placed)
    }),
  }
}

function isGiven(state: SudokuState, cell: number): boolean {
  return state.puzzle.givens[cell] !== EMPTY
}

/**
 * Moves her level for this grid size, once, at the end of the round.
 *
 * Not per cell, which is the tempting thing and would be wrong twice over: a
 * level would swing sixty times in one sitting, and every one of those swings
 * would be about a cell rather than about a puzzle. What she did here is
 * finish a grid, or not.
 *
 * Up for a grid solved without needing to type over her own work much, held
 * for a grid solved the long way round, and down only for one abandoned. A
 * puzzle put down half-finished is not a failure worth demoting her for
 * unless she was nowhere near — hence the share, rather than the fact.
 */
function levelAfter(level: number, state: SudokuState): number {
  const toFill = cellsToFill(state.puzzle)

  if (state.status === 'finished') {
    // Roughly one correction per four cells is a child working steadily; more
    // than that and the grid was a fight worth repeating at the same level.
    return state.changes * 4 <= toFill ? Math.min(MAX_LEVEL, level + 1) : level
  }

  const filled = state.grid.filter((value, cell) => value !== EMPTY && !isGiven(state, cell)).length

  // Got over halfway and put it down: no shame in that, and no easier puzzles
  // for it either.
  return filled * 2 >= toFill ? level : Math.max(MIN_LEVEL, level - 1)
}

/** Only the skill this round practised, for writing levels back. */
export function sudokuLevelsTouched(state: SudokuState): SkillLevels {
  const key = sudokuSkillOf(state.puzzle.size).key

  return { [key]: levelAfter(levelOf(state.levels, key), state) }
}

/**
 * How the round is described to a parent, once.
 *
 * One row per cell she filled would be sixty rows for a 9×9 and would bury
 * every other game's handful. What is worth reading is the grid: how big, how
 * it went, how long, and how much rubbing out it took.
 */
export function sudokuAttempt(
  state: SudokuState,
  playerId: string,
  endedAt: number,
): SudokuAttempt {
  const skill = sudokuSkillOf(state.puzzle.size)
  const toFill = cellsToFill(state.puzzle)
  const filled = state.grid.filter((value, cell) => value !== EMPTY && !isGiven(state, cell)).length

  return {
    skillKey: skill.key,
    prompt: [
      `A ${state.puzzle.size}×${state.puzzle.size} grid with ${state.puzzle.givens.filter((value) => value !== EMPTY).length} squares filled in already`,
      state.puzzle.hardest ? `needing ${inWords(state.puzzle.hardest)}` : null,
      state.status === 'finished'
        ? `solved with ${state.changes} changes of mind`
        : `left with ${toFill - filled} of ${toFill} squares empty`,
    ]
      .filter(Boolean)
      .join(', '),
    // The grid was either finished or it was not, so what was "expected" is
    // every square and what was "given" is how many she got to.
    expected: toFill,
    given: filled,
    correct: state.status === 'finished',
    elapsedMs: Math.max(0, endedAt - state.startedAt),
    playerId,
  }
}

/** The technique names, for a parent rather than for a solver. */
function inWords(technique: string): string {
  if (technique === 'naked-single') return 'plain scanning'
  if (technique === 'hidden-single') return 'working out where one could go'

  return 'ruling two squares out for something else'
}

export const sudokuEngine: GameEngine<SudokuConfig, SudokuState, SudokuAction> = {
  key: 'sudoku',
  actionSchema: sudokuAction,

  createInitialState(config: SudokuConfig, players: readonly Player[], rng: Rng): SudokuState {
    const size = config.size ?? DEFAULT_SUDOKU_SIZE
    const levels = config.levels ?? {}
    // Dealt from where she actually stands at this size, and only this size.
    // A nine-by-nine she has never touched says nothing about her four-by-four.
    const level = levelOf(levels, sudokuSkillOf(size).key)
    const puzzle = buildSudoku(sudokuShapeFor(size, level), rng)

    return {
      puzzle,
      grid: puzzle.givens,
      levels,
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt ?? 0,
      changes: 0,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    const size = state.puzzle.size

    if (action.type === 'clear') {
      // Nothing of hers on the board, so nothing to rub out. Refused rather
      // than accepted as a no-op, so the move log does not fill with resets
      // from somebody leaning on the key.
      if (state.grid.every((value, cell) => value === (state.puzzle.givens[cell] ?? EMPTY))) {
        return reject(state, 'already-played')
      }

      return accept({ ...state, grid: state.puzzle.givens, changes: state.changes + 1 })
    }

    if (action.cell >= size * size) return reject(state, 'invalid-action')
    // A value the grid does not have is a client sending nonsense rather than
    // a mistake she made — a 7 on a 4×4 is not something she can press.
    if (action.value !== null && action.value > size) return reject(state, 'invalid-action')

    /*
     * The puzzle is not hers to edit.
     *
     * Refused here rather than merely disabled in the browser, because the
     * browser is not the only thing that can send this — and a round replayed
     * against a doctored given would score a grid she never played.
     */
    if (isGiven(state, action.cell)) return reject(state, 'already-played')

    const was = state.grid[action.cell] ?? EMPTY
    const now = action.value ?? EMPTY
    // Nothing to do, and nothing to record. A repeated keypress on the same
    // cell is a keyboard repeating, not a decision.
    if (was === now) return reject(state, 'already-played')

    const grid = [...state.grid]
    grid[action.cell] = now

    const finished = isGridSolved(grid, shapeOf(size))

    return accept({
      ...state,
      grid,
      changes: was === EMPTY ? state.changes : state.changes + 1,
      status: finished ? 'finished' : 'playing',
      turn: { ...state.turn, currentPlayerId: finished ? null : state.turn.currentPlayerId },
    })
  },

  isTerminal(state) {
    return state.status === 'finished'
  },

  summarise(state, endedAt): PlaySummary {
    return {
      gameKey: 'sudoku',
      result: state.status === 'finished' ? 'won' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: `${state.puzzle.size}×${state.puzzle.size}`,
    }
  },
}
